<template>
  <footer class="table-pagination">
    <div class="pagination-summary">
      {{ startItem }}-{{ endItem }} of {{ total }}
    </div>
    <label class="page-size-control">
      <span>Rows per page</span>
      <select :value="pageSize" @change="changePageSize">
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
      </select>
    </label>
    <div class="page-controls">
      <button class="page-step" :disabled="safePage <= 1" @click="$emit('update:page', safePage - 1)">Prev</button>
      <template v-for="item in visiblePages" :key="item.key">
        <span v-if="item.type === 'ellipsis'" class="page-ellipsis">...</span>
        <button
          v-else
          :class="['page-number', { active: item.page === safePage }]"
          :aria-current="item.page === safePage ? 'page' : undefined"
          @click="$emit('update:page', item.page)"
        >
          {{ item.page }}
        </button>
      </template>
      <button class="page-step" :disabled="safePage >= totalPages" @click="$emit('update:page', safePage + 1)">Next</button>
    </div>
  </footer>
</template>

<script setup>
import { computed, watch } from 'vue'

const props = defineProps({
  total: { type: Number, required: true },
  page: { type: Number, required: true },
  pageSize: { type: Number, required: true },
})
const emit = defineEmits(['update:page', 'update:page-size'])

const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)))
const safePage = computed(() => Math.min(Math.max(props.page, 1), totalPages.value))
const startItem = computed(() => props.total ? (safePage.value - 1) * props.pageSize + 1 : 0)
const endItem = computed(() => Math.min(props.total, safePage.value * props.pageSize))
const visiblePages = computed(() => {
  if (totalPages.value < 5) {
    return Array.from({ length: totalPages.value }, (_, index) => pageItem(index + 1))
  }
  const pages = new Set([1, totalPages.value, safePage.value - 1, safePage.value, safePage.value + 1])
  if (safePage.value <= 4) {
    pages.add(2)
    pages.add(3)
    pages.add(4)
    pages.add(5)
  }
  if (safePage.value >= totalPages.value - 3) {
    pages.add(totalPages.value - 4)
    pages.add(totalPages.value - 3)
    pages.add(totalPages.value - 2)
    pages.add(totalPages.value - 1)
  }
  const sortedPages = [...pages].filter(page => page >= 1 && page <= totalPages.value).sort((a, b) => a - b)
  return sortedPages.flatMap((page, index) => {
    const previous = sortedPages[index - 1]
    const items = []
    if (previous && page - previous > 1) items.push({ type: 'ellipsis', key: `ellipsis-${previous}-${page}` })
    items.push(pageItem(page))
    return items
  })
})

watch(safePage, value => {
  if (value !== props.page) emit('update:page', value)
})

function changePageSize(event) {
  emit('update:page-size', Number(event.target.value))
  emit('update:page', 1)
}

function pageItem(page) {
  return { type: 'page', page, key: `page-${page}` }
}
</script>

<style scoped>
.table-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  border-top: 1px solid var(--border);
  padding-top: var(--space-4);
  color: var(--ink-muted);
  font-size: 0.8125rem;
}

.pagination-summary {
  margin-right: auto;
}

.page-size-control {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  white-space: nowrap;
}

.page-size-control select {
  width: auto;
  min-width: 4.875rem;
  min-height: 2.25rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  padding: var(--space-2) var(--space-3);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.page-step,
.page-number {
  min-height: 2.25rem;
  border: 1px solid transparent;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--ink-muted);
  font: inherit;
  font-weight: 600;
}

.page-step {
  padding: var(--space-2) var(--space-3);
}

.page-number {
  min-width: 2.25rem;
  padding: var(--space-2);
}

.page-step:hover:not(:disabled),
.page-step:focus-visible:not(:disabled),
.page-number:hover,
.page-number:focus-visible {
  background: var(--surface-muted);
  color: var(--ink);
}

.page-number.active {
  border-color: var(--accent);
  background: var(--accent);
  color: var(--surface);
}

.page-step:disabled {
  opacity: 0.45;
}

.page-ellipsis {
  min-width: 1.5rem;
  color: var(--ink-muted);
  text-align: center;
}

@media (max-width: 767px) {
  .table-pagination {
    align-items: stretch;
    flex-direction: column;
  }

  .pagination-summary {
    margin-right: 0;
  }

  .page-size-control select,
  .page-step,
  .page-number {
    min-height: 44px;
  }

  .page-number {
    min-width: 44px;
  }

  .page-controls {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
}
</style>
